import { computed, defineComponent, onMounted, ref, watch, } from '@vue/composition-api';
import { GenericAGTable } from '@/components/Table/AGTable/AGTable.vue';
import { getPlacesList, } from '@/api/requests/places';
import { useBrand } from '@/composables';
import { usePlaceResourcesStore } from '@/store/place-resources';
import { DummyFilter } from '@/components/Table/dummy-filter';
import { useResourcesTableQuery } from '@/views/Brand/Applications/Components/Resources/_composables/use-resources-table-query';
import SelectionCell from '@/views/Brand/Applications/Components/Resources/_cell-renderers/SelectionCell.vue';
import PreviewCell from '@/views/Brand/Applications/Components/Resources/_cell-renderers/PreviewCell.vue';
import FloatingTextFilter from '@/components/Table/AGTable/_components/FloatingTextFilter.vue';
import PlacePreview from '@/views/Brand/Places/PlacePreview.vue';
import Pagination from '@/components/Pagination/Pagination.vue';
import PaginationSelect from '@/components/Inputs/PaginationSelect/PaginationSelect.vue';
export default defineComponent({
    components: {
        AgTable: GenericAGTable(),
        PlacePreview,
        Pagination,
        PaginationSelect,
        // eslint-disable-next-line vue/no-unused-components
        SelectionCell,
        // eslint-disable-next-line vue/no-unused-components
        PreviewCell,
        // eslint-disable-next-line vue/no-unused-components
        FloatingTextFilter,
    },
    props: {
        reportId: {
            type: String,
        },
        query: {
            type: Object,
            default: () => ({}),
        },
        containerSelector: {
            type: String,
            required: true,
        },
    },
    setup(props, ctx) {
        const { brand } = useBrand();
        const { isPlaceSelected, onPlaceSelection } = usePlaceResourcesStore();
        const gridApi = ref(null);
        const isLoading = ref(false);
        const rows = ref([]);
        const totalCount = ref(0);
        const previewPlaceId = ref(null);
        const { page, perPage, query: tableQuery, handleFilterChange: handleQueryFilterChange, handleSort, resetSort, } = useResourcesTableQuery({
            defaultPerPage: 25,
            filterParams: {
                number: 'number',
                placeName: 'placeName',
                address: 'address',
                structureName: 'structureName',
            },
            fieldMap: {
                number: 'number',
                placeName: 'name',
                address: 'address',
                structureName: 'structure',
            },
        });
        const apiQuery = computed(() => {
            const q = { ...tableQuery.value };
            delete q.perPage;
            return {
                ...q,
                brandId: brand.value?.id,
                ...props.query,
                ...(props.reportId && { relatedReportId: props.reportId }),
                limit: perPage.value,
                page: page.value,
            };
        });
        const totalPages = computed(() => Math.ceil(totalCount.value / perPage.value));
        const columns = computed(() => [
            {
                headerName: '',
                field: 'id',
                cellRenderer: 'SelectionCell',
                cellRendererParams: {
                    isSelected: (data) => isPlaceSelected(data),
                    onToggle: (data) => {
                        onPlaceSelection([data.id], [data]);
                        gridApi.value?.refreshCells({ columns: ['id'] });
                    },
                },
                width: 60,
                minWidth: 60,
                maxWidth: 60,
                sortable: false,
                filter: false,
                suppressMovable: true,
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
            },
            {
                field: 'number',
                colId: 'number',
                headerName: '#',
                cellClass: 'ag-cell--bold',
                sortable: true,
                width: 140,
                minWidth: 120,
                flex: 0,
                filter: DummyFilter,
                floatingFilter: true,
                floatingFilterComponent: 'FloatingTextFilter',
                suppressFloatingFilterButton: true,
            },
            {
                field: 'name',
                colId: 'placeName',
                headerName: ctx.root.$t('place-form.place-name').toString(),
                flex: 1,
                minWidth: 200,
                sortable: true,
                filter: DummyFilter,
                floatingFilter: true,
                floatingFilterComponent: 'FloatingTextFilter',
                suppressFloatingFilterButton: true,
            },
            {
                field: 'address',
                colId: 'address',
                headerName: ctx.root.$t('object-list-view.address').toString(),
                valueGetter: (params) => {
                    const data = params.data;
                    if (!data)
                        return '-';
                    const parts = [];
                    if (data.city)
                        parts.push(data.city);
                    if (data.address)
                        parts.push(data.address);
                    return parts.join(', ') || '-';
                },
                flex: 1,
                minWidth: 150,
                sortable: true,
                filter: DummyFilter,
                floatingFilter: true,
                floatingFilterComponent: 'FloatingTextFilter',
                suppressFloatingFilterButton: true,
            },
            {
                field: 'structure.name',
                colId: 'structureName',
                headerName: ctx.root.$t('place-form.structure').toString(),
                valueGetter: (params) => {
                    const data = params.data;
                    if (!data)
                        return '-';
                    return data.structure?.name || '-';
                },
                flex: 1,
                minWidth: 150,
                sortable: true,
                filter: DummyFilter,
                floatingFilter: true,
                floatingFilterComponent: 'FloatingTextFilter',
                suppressFloatingFilterButton: true,
            },
            {
                headerName: '',
                field: 'preview',
                cellRenderer: 'PreviewCell',
                cellRendererParams: {
                    isExpanded: (data) => previewPlaceId.value === data.id,
                    onTogglePreview: (data) => {
                        previewPlaceId.value =
                            previewPlaceId.value === data.id ? null : data.id;
                        gridApi.value?.refreshCells({ columns: ['preview'], force: true });
                    },
                },
                width: 60,
                minWidth: 60,
                maxWidth: 60,
                sortable: false,
                filter: false,
                suppressMovable: true,
            },
        ]);
        let fetchId = 0;
        const fetchItems = async () => {
            const currentFetchId = ++fetchId;
            isLoading.value = true;
            gridApi.value?.showLoadingOverlay();
            try {
                const result = await getPlacesList(apiQuery.value);
                if (currentFetchId !== fetchId)
                    return;
                rows.value = result.results;
                totalCount.value = result.pagination.total;
            }
            catch (err) {
                console.error('Error fetching places:', err);
            }
            finally {
                if (currentFetchId === fetchId) {
                    isLoading.value = false;
                    gridApi.value?.hideOverlay();
                }
            }
        };
        const changePage = (newPage) => {
            page.value = newPage;
        };
        const changePerPage = (newPerPage) => {
            perPage.value = newPerPage;
            page.value = 1;
        };
        const handleFilterChange = (filterModel) => {
            handleQueryFilterChange(filterModel);
        };
        const handleSortChange = (field, sortDir) => {
            handleSort(field, sortDir);
        };
        const handleResetSort = () => {
            resetSort();
        };
        const onGridReady = (event) => {
            gridApi.value = event.api;
        };
        const closePreview = () => {
            previewPlaceId.value = null;
            gridApi.value?.refreshCells({ columns: ['preview'], force: true });
        };
        onMounted(fetchItems);
        watch(apiQuery, fetchItems, { deep: true });
        return {
            columns,
            rows,
            totalCount,
            totalPages,
            page,
            perPage,
            previewPlaceId,
            handleFilterChange,
            handleSortChange,
            handleResetSort,
            onGridReady,
            changePage,
            changePerPage,
            closePreview,
        };
    },
});
